import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import {
  orderApiCart,
  menuApiPizzaBuilder,
  CartYum,
  PizzaBuilderOptions
} from '@pizza-hut-us-development/client-core';
import { useCartRail } from '@noqo/clients/web/providers/CartRailProvider';

import { localizationSelectors } from '@/localization/localizationSelectors';
import { closeModal, openLocalizationRail, openModal } from '@/localization/actions';
import { NATIONAL } from '@/localization/constants';

import { openCartRail } from '@/clientCore/redux/rail/CartRailSlice';
import useFeatureFlagOverride from '@/hooks/useFeatureFlagOverride';
import { orderSelectors } from '@/clientCore/redux/selectors/orderSelectors';
import { isRedemptionWarning, isResolvableWarning } from '@/clientCore/cart/components/CartRail/components/CartContent/components/YumAddedCoupons/helpers';

import Routes from '@/router/routes';
import { RootState } from '@/rootStateTypes';

interface ApplyPromoCodeResponse {
  data: CartYum;
}

interface PizzaBuilderOptionsResponse {
  data: PizzaBuilderOptions;
  error: string;
  status: string;
  endpointName: 'getPizzaBuilder';
  requestId: string;
  originalArgs: {
    itemId: string;
    storeNumber: string;
  };
  startedTimeStamp: number;
  isUninitialized: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
}

export const useMenuPizzaPage = (id: string | null) => {
  const dispatch = useDispatch();
  const router = useRouter();

  const { enabled: isNewNOQOCartEnabled } = useFeatureFlagOverride('noqo-295-web-cart-rail-v2');
  const { openCartRail: openCartRailV2 } = useCartRail();

  const isLocalized = useSelector((state: RootState) => state.domain.localization.localizedStore !== NATIONAL);
  const storeDetails = useSelector(localizationSelectors.storeDetails);
  const cart = useSelector(orderSelectors.cart);

  const handleApplyPromoCodeResponse = useCallback((response: ApplyPromoCodeResponse, itemId: string) => {
    let isSuccessfulWarning = false;

    const successApplyPromoCodeModal = {
      title: 'Coupon Applied!',
      body: 'Your coupon has been added to the cart.',
      cta: {
        text: 'Ok'
      },
      onClose: () => {
        if (isNewNOQOCartEnabled) {
          openCartRailV2();
        } else {
          dispatch(openCartRail());
        }
      }
    };

    const errorApplyPromoCodeModal = {
      title: 'Coupon Not Applied!',
      body: 'Your coupon could not be added to the cart.',
      cta: {
        text: 'Ok'
      },
      onClose: () => dispatch(closeModal())
    };

    if ('data' in response) {
      isSuccessfulWarning = !!response.data.warnings?.find((warning) => {
        if ('code' in warning && warning.code === itemId) {
          if (isRedemptionWarning(warning)) return isResolvableWarning(warning);
        }
        return false;
      });
    }

    if (isSuccessfulWarning) {
      dispatch(openModal({
        ...successApplyPromoCodeModal
      }));
    } else {
      dispatch(openModal({
        ...errorApplyPromoCodeModal
      }));
    }
  }, [dispatch, isNewNOQOCartEnabled, openCartRailV2]);

  const determineTypeOfId = useCallback(async (itemId: string, storeNumber: string) => {
    // first check to see if the id is a pizza code (pizza builder data is fetched directly from Redux's cache so this call resolves immediately)
    const getPizzaBuilderResponse = await dispatch(menuApiPizzaBuilder.endpoints.getPizzaBuilder.initiate({
      itemId,
      storeNumber
    }) as unknown as PizzaBuilderOptionsResponse);

    // if the pizza builder response data returns an error, assume it's a redemption coupon code
    if (getPizzaBuilderResponse.error) {
      const applyPromoCodeResponse = await dispatch(orderApiCart.endpoints.applyPromoCode.initiate(itemId)) as unknown as ApplyPromoCodeResponse;
      handleApplyPromoCodeResponse(applyPromoCodeResponse, itemId);

    // if the pizza builder response data returns successfully, navigate to the pizza builder page with the pizza product code
    } else {
      router.push(`${Routes.MENU.PIZZA_BUILDER}?id=${itemId}`);
    }
  }, [dispatch, router, handleApplyPromoCodeResponse]);

  useEffect(() => {
    if (!storeDetails?.storeNumber || !id || !cart?.storeNumber?.length) {
      return;
    }

    determineTypeOfId(id, storeDetails.storeNumber);
  }, [storeDetails?.storeNumber, determineTypeOfId, cart?.storeNumber, id]);

  useEffect(() => {
    if (id && !isLocalized) {
      dispatch(openLocalizationRail());
    }
  }, [id, isLocalized, dispatch]);
};
